import { useParams, Link } from 'react-router-dom';
import PageSEO from '@/components/feature/PageSEO';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/feature/ArticleCard';
import { getArticleById, getRelatedArticles, categories } from '@/mocks/articles';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = getArticleById(id || '');

  if (!article) {
    return (
      <div className="pt-20 w-full px-4 md:px-6 py-20 text-center">
        <div className="max-w-7xl mx-auto">
          <h1 className="font-heading text-2xl font-bold text-foreground-900 mb-4">文章不存在</h1>
          <Link to="/" className="text-primary-600 hover:text-primary-700 text-sm cursor-pointer">
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const relatedArticles = getRelatedArticles(article, 3);
  const category = categories.find((c) => c.slug === article.category);
  const categoryLabel = category?.name || article.category;

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.image,
    datePublished: article.date,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    publisher: {
      '@type': 'Organization',
      name: '今日同人库',
      url: 'https://todaydoujin.com/',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://todaydoujin.com/article/${article.id}`,
    },
    wordCount: article.content.length,
    timeRequired: `PT${article.readTime}M`,
  };

  return (
    <>
      <PageSEO
        title={article.title}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonicalUrl={`/article/${article.id}`}
        ogType="article"
        ogImage={article.image}
        jsonLd={jsonLd}
      />

      <div className="pt-20">
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: categoryLabel, href: `/category/${article.category}` },
            { label: article.title },
          ]}
        />

        <article className="w-full px-4 md:px-6 py-8">
          <div className="max-w-4xl mx-auto">
            <div className="mb-6">
              <div className="flex items-center gap-3 mb-4">
                <Link
                  to={`/category/${article.category}`}
                  className="inline-block px-3 py-1 bg-primary-100/70 text-primary-700 text-xs font-medium rounded-md whitespace-nowrap hover:bg-primary-200 transition-colors cursor-pointer"
                >
                  {categoryLabel}
                </Link>
                {article.featured && (
                  <span className="inline-block px-3 py-1 bg-accent-100/80 text-accent-700 text-xs font-medium rounded-md whitespace-nowrap">
                    精选
                  </span>
                )}
              </div>

              <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mb-4 leading-tight">
                {article.title}
              </h1>

              <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500 mb-6">
                <span className="flex items-center gap-1.5">
                  <i className="ri-user-line"></i>
                  {article.author}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-calendar-line"></i>
                  {article.date}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-time-line"></i>
                  阅读约 {article.readTime} 分钟
                </span>
              </div>

              <div className="flex flex-wrap gap-2 mb-6">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-2.5 py-1 bg-secondary-100/80 text-secondary-700 text-xs rounded-md whitespace-nowrap"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </div>

            <div className="rounded-lg overflow-hidden mb-8">
              <img
                src={article.image}
                alt={article.title}
                title={`${article.title} - 今日同人库`}
                className="w-full aspect-[16/9] object-cover object-top"
              />
            </div>

            <div
              className="prose prose-base md:prose-lg max-w-none text-foreground-800 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="mt-10 pt-8 border-t border-background-200/70">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-3">
                  <span className="text-sm text-foreground-500">分享到：</span>
                  {[
                    { icon: 'ri-weibo-line', label: '微博' },
                    { icon: 'ri-wechat-line', label: '微信' },
                    { icon: 'ri-qq-line', label: 'QQ' },
                    { icon: 'ri-link', label: '复制链接' },
                  ].map((s) => (
                    <button
                      key={s.label}
                      className="w-8 h-8 flex items-center justify-center rounded-lg bg-background-100 hover:bg-background-200 text-foreground-500 transition-colors cursor-pointer"
                      aria-label={s.label}
                    >
                      <i className={`${s.icon} text-sm`}></i>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
            <div className="max-w-7xl mx-auto">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-6">
                相关推荐
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5" data-product-shop>
                {relatedArticles.map((a) => (
                  <ArticleCard key={a.id} article={a} />
                ))}
              </div>
            </div>
          </section>
        )}
      </div>
    </>
  );
}